export const DentalFormFieldVariables = {
    pmsName: '{PMSName}',
    procedureTypes: '{procedureTypes}',
    medicalIntegrationConfigId: '{MedicalIntegrationConfigId}',
    cancellationCode: '{DenticonCancelCode}',
    referralTypeCode: '{denticon-referral-type-code}',
    defaultOfficeId: '{defaultOfficeId}',
    defaultProviderId: '{DefaultProviderId}',
    callBackNumberOverride: '{callBackNumberOverride}',
    transferEmergencyAppointments: '{TransferEmergencyAppointments}',
    officeAppointmentToEmail: '{{OfficeAppointmentToEmail}}'
};

export const DentalFormFieldVariableList = Object.keys(DentalFormFieldVariables)
    .map(key => DentalFormFieldVariables[key as keyof typeof DentalFormFieldVariables]);

export type DentalFormFieldValues = { [associatedVariable: string]: string | null | undefined };

export type DentalSandboxWarningType =
    'sandbox-procedure-types'
    | 'sandbox-medical-config'
    | 'sandbox-cancellation-code'
    | 'sandbox-referral-code'
    | 'sandbox-office-id'
    | 'default-provider-id';

export interface DentalSandboxWarning {
    type: DentalSandboxWarningType;
    label: string;
    message: string;
    level: 'p1';
}

type DentalPms = 'denticon' | 'dentrix-ascend' | 'cloud9';

const procedureTypeSandboxValues: { [pms in DentalPms]: unknown[] } = {
    denticon: [{
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: 30,
        procedureTypeDescription: 'Anything related to a routine visit, regular cleaning, check-up, dental visit, or cleaning.',
        procedureTypeId: '39'
    }],
    'dentrix-ascend': [{
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: 30,
        procedureTypeDescription: 'Anything related to a routine visit, regular cleaning, check-up,dental visit, or cleaning.',
        procedureTypeId: '25000000391638'
    }],
    cloud9: [{
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: 30,
        procedureTypeDescription: 'Always extract this type no matter what the patient says. For example, if they say treatment plan, extract this.',
        procedureTypeId: 'voicify-treatment-plan'
    }, {
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: 30,
        procedureTypeDescription: 'ADJ - 30',
        procedureTypeId: '1bdfbc53-7c4c-4551-ab90-f5dc7eb7551a',
        DefaultProcedureForExistingPatients: true,
        isCleaningAppointment: true
    }]
};

const medicalIntegrationSandboxValues: { [pms in DentalPms]: string } = {
    denticon: '22FA374D-7BA2-439F-A1AD-F4712DD882EF',
    'dentrix-ascend': '87AC3583-8BFC-439D-9F0B-C7A450CF5E2A',
    cloud9: '33e9212f-3b6b-407b-9a9a-bac9d2eee703'
};

export const isSandboxMedicalIntegrationConfigId = (value?: string | null): boolean =>
    !!value && Object.keys(medicalIntegrationSandboxValues).some(pms => value.trim().toLowerCase() === medicalIntegrationSandboxValues[pms].toLowerCase());

// Denticon's sandbox admin cancellation code ID and referral type code
export const isSandboxCancellationCode = (value?: string | null): boolean => value?.trim() === '473';

export const isSandboxReferralTypeCode = (value?: string | null): boolean => {
    const code = value?.trim().toLowerCase();
    return code === 'roo6' || code === 'r006';
};

const defaultOfficeSandboxValues:{ [pms in DentalPms]: string } = {
    denticon: '266',
    'dentrix-ascend': '25000000000392',
    cloud9: '5f274ff6-90a6-5ece-f3b5-41b71a8ffaa4'
};

const cloud9DefaultProviderSandboxValue = 'd16a4bba-4701-4463-a287-72b692406ae6';

const normalizePmsName = (value: string | null | undefined): DentalPms | null => {
    const normalized = (value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (normalized === 'denticon') return 'denticon';
    if (normalized === 'dentrixascend') return 'dentrix-ascend';
    if (normalized === 'cloud9') return 'cloud9';
    return null;
};

const canonicalizeJson = (value: any): any => {
    if (Array.isArray(value)) {
        return value.map(canonicalizeJson);
    }
    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((result, key) => {
            result[key] = canonicalizeJson(value[key]);
            return result;
        }, {} as { [key: string]: any });
    }
    return value;
};

const isSandboxProcedureTypes = (value: string | null | undefined, pms: DentalPms): boolean => {
    if (!value) return false;
    try {
        return JSON.stringify(canonicalizeJson(JSON.parse(value))) === JSON.stringify(canonicalizeJson(procedureTypeSandboxValues[pms]));
    } catch {
        return false;
    }
};

const equalsIgnoreCase = (value: string | null | undefined, expected: string): boolean => (
    typeof value === 'string' && value.trim().toLowerCase() === expected.toLowerCase()
);

export const isSandboxCloud9DefaultProvider = (value?: string | null): boolean => equalsIgnoreCase(value, cloud9DefaultProviderSandboxValue);

export const resolveEffectiveDentalFormFieldValues = (
    locationValues?: DentalFormFieldValues,
    sharedValues?: DentalFormFieldValues
): DentalFormFieldValues => DentalFormFieldVariableList.reduce((resolved, associatedVariable) => {
    const isMeaningful = (value: string | null | undefined) => typeof value === 'string'
        && value.trim() !== ''
        && value.trim() !== '-1'
        && value.trim() !== associatedVariable;
    const locationValue = locationValues?.[associatedVariable];
    const sharedValue = sharedValues?.[associatedVariable];
    resolved[associatedVariable] = isMeaningful(locationValue) ? locationValue : isMeaningful(sharedValue) ? sharedValue : undefined;
    return resolved;
}, {} as DentalFormFieldValues);

export const getDentalSandboxWarnings = (values: DentalFormFieldValues): DentalSandboxWarning[] => {
    const pms = normalizePmsName(values[DentalFormFieldVariables.pmsName]);
    if (!pms) return [];

    const warnings: DentalSandboxWarning[] = [];
    if (isSandboxProcedureTypes(values[DentalFormFieldVariables.procedureTypes], pms)) {
        warnings.push({
            type: 'sandbox-procedure-types',
            label: 'Sandbox Procedure Types',
            message: 'Procedure Types is using the sandbox configuration for this PMS. Replace it with the client\'s production procedure types.',
            level: 'p1'
        });
    }

    if (equalsIgnoreCase(values[DentalFormFieldVariables.medicalIntegrationConfigId], medicalIntegrationSandboxValues[pms])) {
        warnings.push({
            type: 'sandbox-medical-config',
            label: 'Sandbox Medical Config',
            message: 'Medical Integration Config ID is using the sandbox value for this PMS. Replace it with the client\'s production configuration ID.',
            level: 'p1'
        });
    }

    if (pms === 'denticon' && isSandboxCancellationCode(values[DentalFormFieldVariables.cancellationCode])) {
        warnings.push({
            type: 'sandbox-cancellation-code',
            label: 'Sandbox Cancellation Code',
            message: 'Admin Cancellation Code ID is using the Denticon sandbox value. Replace it with the client\'s production cancellation code.',
            level: 'p1'
        });
    }

    if (pms === 'denticon' && isSandboxReferralTypeCode(values[DentalFormFieldVariables.referralTypeCode])) {
        warnings.push({
            type: 'sandbox-referral-code',
            label: 'Sandbox Referral Code',
            message: 'Admin Referral Type Code is using the Denticon sandbox value. Replace it with the client\'s production referral code.',
            level: 'p1'
        });
    }

    if (equalsIgnoreCase(values[DentalFormFieldVariables.defaultOfficeId], defaultOfficeSandboxValues[pms])) {
        warnings.push({
            type: 'sandbox-office-id',
            label: 'Sandbox Office ID',
            message: 'Default Office ID is using the sandbox value for this PMS. Replace it with the client\'s production office ID.',
            level: 'p1'
        });
    }

    if (pms === 'cloud9') {
        const defaultProviderId = values[DentalFormFieldVariables.defaultProviderId];
        if (typeof defaultProviderId !== 'string' || defaultProviderId.trim() === '') {
            warnings.push({
                type: 'default-provider-id',
                label: 'Default Provider ID',
                message: 'Admin Default Provider ID is missing for this live Cloud 9 location. Add the client\'s production provider ID.',
                level: 'p1'
            });
        } else if (isSandboxCloud9DefaultProvider(defaultProviderId)) {
            warnings.push({
                type: 'default-provider-id',
                label: 'Default Provider ID',
                message: 'Admin Default Provider ID is using the Cloud 9 sandbox value. Replace it with the client\'s production provider ID.',
                level: 'p1'
            });
        }
    }

    return warnings;
};
